<template>
  <SidebarProvider :open="!isCollapse" class="sidebar-provider">
    <UiSidebar class="sidebar-wrapper" collapsible="icon">
      <SidebarHeader class="sidebar-header">
        <SidebarLogo :is-collapse="isCollapse" @refresh="refreshPage" />
      </SidebarHeader>
      <SidebarContent class="sidebar-content">
        <ScrollArea as="nav" class="sidebar-nav" :class="{ 'sidebar-nav--collapsed': isCollapse }">
          <nav class="sidebar-nav__view">
            <div class="sidebar-top-actions">
              <TooltipProvider :delay-duration="300">
                <Tooltip>
                  <TooltipTrigger as-child>
                    <button
                      type="button"
                      class="sidebar-create-button"
                      :aria-label="t('route.newRecruitingConversation')"
                      @click="handleNewConversation"
                    >
                      <Plus class="sidebar-button-icon" />
                      <span v-if="!isCollapse">{{ t('route.newRecruitingConversation') }}</span>
                    </button>
                  </TooltipTrigger>
                  <TooltipContent side="right" :side-offset="10">
                    {{ t('route.newRecruitingConversation') }}
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
              <TooltipProvider v-if="!isCollapse" :delay-duration="300">
                <Tooltip>
                  <TooltipTrigger as-child>
                    <Button
                      type="button"
                      variant="ghost"
                      class="sidebar-notification-button"
                      :aria-label="
                        hasNotifications
                          ? t('header.unreadNotifications')
                          : t('header.notifications')
                      "
                      @click="openNotificationDrawer"
                    >
                      <span class="sidebar-notification-badge">
                        <AppSvgIcon :src="NotificationBellIcon" :size="16" />
                        <span
                          v-if="hasNotifications"
                          class="sidebar-notification-badge__content"
                          aria-hidden="true"
                        />
                      </span>
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent side="right" :side-offset="10">
                    {{ t('header.notifications') }}
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>

            <SidebarGroup class="sidebar-section">
              <SidebarGroupLabel v-if="!isCollapse" class="sidebar-section__title">
                {{ t('route.recruitingSection') }}
              </SidebarGroupLabel>
              <SidebarGroupContent>
                <SidebarMenu>
                  <SidebarMenuItem v-if="enableJobs">
                    <TooltipProvider :delay-duration="300">
                      <Tooltip>
                        <TooltipTrigger as-child>
                          <button
                            type="button"
                            class="sidebar-nav-item"
                            :class="{ 'is-active': isRecruitingProjectsActive }"
                            :aria-label="t('route.recruitingProjects')"
                            @click="handleMenuSelect('/manage/jobs?filter=all')"
                          >
                            <span class="sidebar-nav-item__icon"
                              ><AppSvgIcon :src="SidebarAgentIcon" :size="16"
                            /></span>
                            <span v-if="!isCollapse" class="sidebar-nav-item__label">{{
                              t('route.recruitingProjects')
                            }}</span>
                            <span
                              v-if="!isCollapse && totalJobsCount > 0"
                              class="sidebar-nav-item__badge"
                            >
                              {{ totalJobsCount }}
                            </span>
                          </button>
                        </TooltipTrigger>
                        <TooltipContent side="right" :side-offset="10">
                          {{ t('route.recruitingProjects') }}
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </SidebarMenuItem>
                  <SidebarMenuItem>
                    <TooltipProvider :delay-duration="300">
                      <Tooltip>
                        <TooltipTrigger as-child>
                          <button
                            type="button"
                            class="sidebar-nav-item"
                            :class="{ 'is-active': isTalentPoolActive }"
                            :aria-label="t('route.talentGroup')"
                            @click="handleMenuSelect('/manage/talent-pool')"
                          >
                            <span class="sidebar-nav-item__icon"
                              ><AppSvgIcon :src="SidebarUsersIcon" :size="16"
                            /></span>
                            <span v-if="!isCollapse" class="sidebar-nav-item__label">{{
                              t('route.talentGroup')
                            }}</span>
                            <span
                              v-if="!isCollapse && allCandidatesCount > 0"
                              class="sidebar-nav-item__badge"
                            >
                              {{ allCandidatesCount }}
                            </span>
                          </button>
                        </TooltipTrigger>
                        <TooltipContent side="right" :side-offset="10">
                          {{ t('route.talentGroup') }}
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </SidebarMenuItem>
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>

            <div v-if="!isCollapse" class="sidebar-section sidebar-section--recent">
              <button
                type="button"
                class="sidebar-section__title sidebar-section__title--toggle"
                :aria-expanded="isRecentConversationsOpen"
                @click="isRecentConversationsOpen = !isRecentConversationsOpen"
              >
                <span>{{ t('route.recentConversations') }}</span>
                <span
                  class="sidebar-section__title-chevron"
                  :class="{ 'is-open': isRecentConversationsOpen }"
                  aria-hidden="true"
                >
                  <ChevronDown />
                </span>
              </button>
              <ScrollArea type="auto" v-if="isRecentConversationsOpen" class="sidebar-recent-list">
                <TooltipProvider
                  v-for="job in recentAgentConversations"
                  :key="job.id"
                  :delay-duration="300"
                >
                  <Tooltip>
                    <TooltipTrigger as-child>
                      <button
                        type="button"
                        class="sidebar-recent-item"
                        :class="{ 'is-active': isRecentConversationActive(job) }"
                        :aria-label="getJobDisplayName(job)"
                        @click="handleRecentConversationSelect(job)"
                      >
                        {{ getJobDisplayName(job) }}
                      </button>
                    </TooltipTrigger>
                    <TooltipContent side="right" :side-offset="10">
                      {{ getJobDisplayName(job) }}
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </ScrollArea>
            </div>

            <SidebarGroup class="sidebar-section">
              <SidebarGroupLabel v-if="!isCollapse" class="sidebar-section__title">
                {{ t('route.platformSection') }}
              </SidebarGroupLabel>
              <SidebarGroupContent>
                <SidebarMenu>
                  <SidebarMenuItem>
                    <TooltipProvider :delay-duration="300">
                      <Tooltip>
                        <TooltipTrigger as-child>
                          <button
                            type="button"
                            class="sidebar-nav-item"
                            :class="{ 'is-active': activeMenuIndex === '/manage/workbench' }"
                            :aria-label="t('route.workbench')"
                            @click="handleMenuSelect('/manage/workbench')"
                          >
                            <span class="sidebar-nav-item__icon"><LayoutDashboard /></span>
                            <span v-if="!isCollapse" class="sidebar-nav-item__label">{{
                              t('route.workbench')
                            }}</span>
                          </button>
                        </TooltipTrigger>
                        <TooltipContent side="right" :side-offset="10">
                          {{ t('route.workbench') }}
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </SidebarMenuItem>
                  <SidebarMenuItem v-if="showAiSourcingLab">
                    <TooltipProvider :delay-duration="300">
                      <Tooltip>
                        <TooltipTrigger as-child>
                          <button
                            type="button"
                            class="sidebar-nav-item"
                            :class="{ 'is-restricted': !canOpenAiSourcingLab }"
                            :aria-label="t('route.aiSourcingLab')"
                            :aria-disabled="!canOpenAiSourcingLab"
                            @click="handleMenuSelect(AI_SOURCING_MENU_INDEX)"
                          >
                            <span class="sidebar-nav-item__icon"
                              ><AppSvgIcon :src="AwaitIcon" :size="16"
                            /></span>
                            <span v-if="!isCollapse" class="sidebar-nav-item__label">{{
                              t('route.aiSourcingLab')
                            }}</span>
                            <span v-if="!isCollapse" class="sidebar-nav-item__beta">{{
                              t('route.beta')
                            }}</span>
                          </button>
                        </TooltipTrigger>
                        <TooltipContent side="right" :side-offset="10">
                          {{ t('route.aiSourcingLab') }}
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </SidebarMenuItem>
                  <SidebarMenuItem v-if="enableContentManagement">
                    <TooltipProvider :delay-duration="300">
                      <Tooltip>
                        <TooltipTrigger as-child>
                          <button
                            type="button"
                            class="sidebar-nav-item"
                            :class="{
                              'is-active': activeMenuIndex === '/manage/content-management',
                            }"
                            :aria-label="t('route.contentManagement')"
                            @click="handleMenuSelect('/manage/content-management')"
                          >
                            <span class="sidebar-nav-item__icon"
                              ><AppSvgIcon :src="SidebarSettingsIcon" :size="16"
                            /></span>
                            <span v-if="!isCollapse" class="sidebar-nav-item__label">{{
                              t('route.contentManagement')
                            }}</span>
                          </button>
                        </TooltipTrigger>
                        <TooltipContent side="right" :side-offset="10">
                          {{ t('route.contentManagement') }}
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </SidebarMenuItem>
                  <SidebarMenuItem v-if="enableEmailCampaign && canEmailInbox">
                    <TooltipProvider :delay-duration="300">
                      <Tooltip>
                        <TooltipTrigger as-child>
                          <button
                            type="button"
                            class="sidebar-nav-item"
                            :class="{ 'is-active': activeMenuIndex === '/manage/inbox' }"
                            :aria-label="t('route.inbox')"
                            @click="handleMenuSelect('/manage/inbox')"
                          >
                            <span class="sidebar-nav-item__icon"><Inbox /></span>
                            <span v-if="!isCollapse" class="sidebar-nav-item__label">{{
                              t('route.inbox')
                            }}</span>
                          </button>
                        </TooltipTrigger>
                        <TooltipContent side="right" :side-offset="10">{{
                          t('route.inbox')
                        }}</TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </SidebarMenuItem>
                  <SidebarMenuItem v-if="enableEmailCampaign && canEmailOutreach">
                    <TooltipProvider :delay-duration="300">
                      <Tooltip>
                        <TooltipTrigger as-child>
                          <button
                            type="button"
                            class="sidebar-nav-item"
                            :class="{ 'is-active': isColdEmailActive }"
                            :aria-label="t('route.coldEmail')"
                            @click="handleColdEmailMenuSelect"
                          >
                            <span class="sidebar-nav-item__icon"><Mail /></span>
                            <span v-if="!isCollapse" class="sidebar-nav-item__label">{{
                              t('route.coldEmail')
                            }}</span>
                            <span
                              v-if="!isCollapse"
                              class="sidebar-nav-item__chevron"
                              :class="{ 'is-open': isColdEmailExpanded }"
                            >
                              <ChevronDown class="size-3.5" />
                            </span>
                          </button>
                        </TooltipTrigger>
                        <TooltipContent side="right" :side-offset="10">
                          {{ t('route.coldEmail') }}
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>

                    <!-- Cold Email Sub Menu (Collapsible) -->
                    <transition name="sidebar-sub-fade">
                      <div v-if="!isCollapse && isColdEmailExpanded" class="sidebar-sub-menu">
                        <button
                          type="button"
                          class="sidebar-sub-item"
                          :class="{ 'is-active': activeColdEmailView === 'list' }"
                          @click="handleColdEmailSubSelect('list')"
                        >
                          <List class="sidebar-sub-item__icon" />
                          <span>{{ t('coldEmail.views.list') }}</span>
                        </button>
                        <button
                          type="button"
                          class="sidebar-sub-item"
                          :class="{ 'is-active': activeColdEmailView === 'edit' }"
                          @click="handleColdEmailSubSelect('edit')"
                        >
                          <Layers class="sidebar-sub-item__icon" />
                          <span>{{ t('coldEmail.views.edit') }}</span>
                        </button>
                        <button
                          type="button"
                          class="sidebar-sub-item"
                          :class="{ 'is-active': activeColdEmailView === 'log' }"
                          @click="handleColdEmailSubSelect('log')"
                        >
                          <History class="sidebar-sub-item__icon" />
                          <span>{{ t('coldEmail.views.log') }}</span>
                        </button>
                        <button
                          type="button"
                          class="sidebar-sub-item"
                          :class="{ 'is-active': activeColdEmailView === 'set' }"
                          @click="handleColdEmailSubSelect('set')"
                        >
                          <Settings class="sidebar-sub-item__icon" />
                          <span>{{ t('coldEmail.views.set') }}</span>
                        </button>
                      </div>
                    </transition>
                  </SidebarMenuItem>
                  <SidebarMenuItem v-if="showOrganizationMenu">
                    <TooltipProvider :delay-duration="300">
                      <Tooltip>
                        <TooltipTrigger as-child>
                          <button
                            type="button"
                            class="sidebar-nav-item"
                            :class="{
                              'is-active': activeMenuIndex === '/manage/organization',
                            }"
                            :aria-label="t('route.organization')"
                            @click="handleMenuSelect('/manage/organization')"
                          >
                            <span class="sidebar-nav-item__icon"><Building2 /></span>
                            <span v-if="!isCollapse" class="sidebar-nav-item__label">{{
                              t('route.organization')
                            }}</span>
                          </button>
                        </TooltipTrigger>
                        <TooltipContent side="right" :side-offset="10">
                          {{ t('route.organization') }}
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </SidebarMenuItem>
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>

            <SidebarGroup v-if="canAdminRead && adminMode" class="sidebar-section">
              <SidebarGroupLabel v-if="!isCollapse" class="sidebar-section__title">
                {{ t('route.adminSection') }}
              </SidebarGroupLabel>
              <SidebarGroupContent>
                <SidebarMenu>
                  <SidebarMenuItem>
                    <button
                      type="button"
                      class="sidebar-nav-item"
                      :class="{
                        'is-active': activeMenuIndex === '/manage/admin/organizations',
                      }"
                      :aria-label="t('route.adminOrganizations')"
                      @click="handleMenuSelect('/manage/admin/organizations')"
                    >
                      <span class="sidebar-nav-item__icon"><Building2 /></span>
                      <span v-if="!isCollapse" class="sidebar-nav-item__label">{{
                        t('route.adminOrganizations')
                      }}</span>
                    </button>
                  </SidebarMenuItem>
                  <SidebarMenuItem>
                    <button
                      type="button"
                      class="sidebar-nav-item"
                      :class="{
                        'is-active': activeMenuIndex === '/manage/admin/resume-import-monitor',
                      }"
                      :aria-label="t('route.resumeImportMonitor')"
                      @click="handleMenuSelect('/manage/admin/resume-import-monitor')"
                    >
                      <span class="sidebar-nav-item__icon"><ChartNoAxesColumn /></span>
                      <span v-if="!isCollapse" class="sidebar-nav-item__label">{{
                        t('route.resumeImportMonitor')
                      }}</span>
                    </button>
                  </SidebarMenuItem>
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>
          </nav>
        </ScrollArea>
      </SidebarContent>
      <SidebarFooter class="sidebar-footer">
        <div
          v-if="canAdminRead"
          class="sidebar-admin-mode"
          :class="{ 'sidebar-admin-mode--collapsed': isCollapse }"
        >
          <TooltipProvider :delay-duration="300">
            <Tooltip>
              <TooltipTrigger as-child>
                <button
                  type="button"
                  class="sidebar-admin-mode__button"
                  :class="{ 'is-active': adminMode }"
                  :aria-label="`${t('header.adminMode')}: ${adminMode ? t('header.on') : t('header.off')}`"
                  @click="handleToggleAdminMode"
                >
                  <Lock class="sidebar-button-icon" />
                  <span v-if="!isCollapse" class="sidebar-admin-mode__label">
                    {{ t('header.adminMode') }}
                  </span>
                  <span v-if="!isCollapse" class="sidebar-admin-mode__state">
                    {{ adminMode ? t('header.on') : t('header.off') }}
                  </span>
                </button>
              </TooltipTrigger>
              <TooltipContent side="right" :side-offset="10">
                {{ `${t('header.adminMode')}: ${adminMode ? t('header.on') : t('header.off')}` }}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
        <!-- 用户个人信息下拉菜单 -->
        <DropdownMenu>
          <div class="sidebar-user" :class="{ 'sidebar-user--collapsed': isCollapse }">
            <DropdownMenuTrigger as-child>
              <button type="button" class="sidebar-user__trigger">
                <span class="sidebar-user__avatar-badge-wrapper">
                  <Avatar class="sidebar-user__avatar size-8! after:border-0!">
                    <AvatarImage
                      :src="userStore.avatarUrl || ''"
                      :alt="userStore.displayName || avatarFallbackText"
                    />
                    <AvatarFallback class="sidebar-user__avatar-fallback">
                      {{ avatarFallbackText }}
                    </AvatarFallback>
                  </Avatar>
                  <span
                    v-if="membershipVipBadge"
                    class="sidebar-user__membership-badge"
                    :class="`sidebar-user__membership-badge--${membershipVipBadge.tone}`"
                  >
                    {{ membershipVipBadge.label }}
                  </span>
                </span>
                <span v-if="!isCollapse" class="sidebar-user__body">
                  <span class="sidebar-user__name">{{ userStore.displayName }}</span>
                  <span class="sidebar-user__email">{{ sidebarUserEmail }}</span>
                </span>
              </button>
            </DropdownMenuTrigger>
          </div>
          <DropdownMenuContent class="sidebar-user__menu" side="top" align="start">
            <div class="sidebar-user__menu-summary">
              <span class="sidebar-user__avatar-badge-wrapper">
                <Avatar class="sidebar-user__avatar size-8! after:border-0!">
                  <AvatarImage
                    :src="userStore.avatarUrl || ''"
                    :alt="userStore.displayName || avatarFallbackText"
                  />
                  <AvatarFallback class="sidebar-user__avatar-fallback">
                    {{ avatarFallbackText }}
                  </AvatarFallback>
                </Avatar>
                <span
                  v-if="membershipVipBadge"
                  class="sidebar-user__membership-badge"
                  :class="`sidebar-user__membership-badge--${membershipVipBadge.tone}`"
                >
                  {{ membershipVipBadge.label }}
                </span>
              </span>
              <div class="sidebar-user__menu-copy">
                <div class="sidebar-user__menu-name">{{ userStore.displayName }}</div>
                <div class="sidebar-user__menu-email">{{ sidebarUserEmail }}</div>
              </div>
            </div>
            <DropdownMenuItem
              class="sidebar-user__menu-item"
              @click="handleUserCommand('points-record')"
            >
              <AppSvgIcon
                class="sidebar-user__menu-icon sidebar-user__points-icon"
                :src="SidebarCreditCardIcon"
                :size="15"
              />
              <span>{{ t('points.balance.contact') }}</span>
              <span
                v-if="userStore.asset?.is_enterprise_mode"
                class="ml-1 rounded bg-amber-50 px-1 py-0.5 text-[10px] font-medium text-amber-700"
              >
                {{ t('organization.enterpriseBadge') }}
              </span>
              <span class="sidebar-user__menu-value" :class="{ 'is-low': isPointsLow }">
                {{ assetLoading ? '...' : pointsDisplay }}
              </span>
            </DropdownMenuItem>
            <DropdownMenuItem
              class="sidebar-user__menu-item"
              @click="handleUserCommand('points-record')"
            >
              <AppSvgIcon
                class="sidebar-user__menu-icon sidebar-user__points-icon"
                :src="AwaitIcon"
                :size="15"
              />
              <span>{{ t('points.balance.aiMatch') }}</span>
              <span
                v-if="userStore.asset?.is_enterprise_mode"
                class="ml-1 rounded bg-amber-50 px-1 py-0.5 text-[10px] font-medium text-amber-700"
              >
                {{ t('organization.enterpriseBadge') }}
              </span>
              <span class="sidebar-user__menu-value">
                {{ assetLoading ? '...' : aiMatchPointsDisplay }}
              </span>
            </DropdownMenuItem>
            <DropdownMenuItem class="sidebar-user__menu-item" @click="handleUserCommand('upgrade')">
              <AppSvgIcon
                class="sidebar-user__menu-icon sidebar-user__upgrade-icon"
                :src="UpgradePlanIcon"
                :size="15"
              />
              <span>{{ t('header.upgrade') }}</span>
            </DropdownMenuItem>
            <DropdownMenuItem
              class="sidebar-user__menu-item"
              @click="handleUserCommand('referral')"
            >
              <Share2 class="sidebar-user__menu-icon" />
              <span>{{ t('route.referral') }}</span>
            </DropdownMenuItem>
            <DropdownMenuItem class="sidebar-user__menu-item" @click="handleUserCommand('profile')">
              <User class="sidebar-user__menu-icon" />
              <span>{{ t('header.profile') }}</span>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarFooter>
    </UiSidebar>

    <Sheet :open="notificationDrawerVisible" @update:open="notificationDrawerVisible = $event">
      <SheetContent
        side="left"
        :show-close-button="false"
        class="sidebar-notification-drawer w-[min(360px,calc(100vw_-_28px))]! p-0 sm:max-w-none"
      >
        <SheetTitle class="sr-only">{{ t('header.notifications') }}</SheetTitle>
        <SheetDescription class="sr-only">{{ t('header.announcementTitle') }}</SheetDescription>
        <div class="notification-drawer">
          <header class="notification-drawer__header">
            <h2>{{ t('header.notifications') }}</h2>
            <button
              type="button"
              class="notification-drawer__close"
              :aria-label="t('header.collapseSidebar')"
              @click="notificationDrawerVisible = false"
            >
              &lt;&lt;
            </button>
          </header>
          <Button
            type="button"
            variant="ghost"
            class="notification-item"
            @click="handleAnnouncementClick"
          >
            <div class="notification-item__content">
              <div class="notification-item__title">{{ t('header.announcementTitle') }}</div>
              <div class="notification-item__desc">{{ t('header.announcementDesc') }}</div>
            </div>
            <span class="notification-item__time">{{ t('header.notificationCurrent') }}</span>
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  </SidebarProvider>
</template>

<script setup lang="ts">
defineOptions({ name: 'MainSidebarNav' })

import { useRoute, useRouter } from 'vue-router'
import { useUserStore } from '@/manage/stores/user'
import { onMounted, onUnmounted, computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import {
  Building2,
  ChartNoAxesColumn,
  ChevronDown,
  History,
  Inbox,
  Layers,
  LayoutDashboard,
  List,
  Lock,
  Mail,
  Plus,
  Settings,
  Share2,
  User,
} from '@lucide/vue'
import { storeToRefs } from 'pinia'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Sheet, SheetContent, SheetDescription, SheetTitle } from '@/components/ui/sheet'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import {
  Sidebar as UiSidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuItem,
  SidebarProvider,
} from '@/components/ui/sidebar'
import AppSvgIcon from '@/manage/components/common/AppSvgIcon.vue'
import SidebarLogo from '@/manage/layouts/components/sidebar/SidebarLogo.vue'
import { onJobListSignal } from '@/manage/utils/jobListSignal'
import { featureFlags } from '@/shared/config/features'
import { websiteLinks } from '@/shared/config/links'
import { usePostHog } from '@/shared/composables/usePostHog'
import { appStorage } from '@/utils/storage/app'
import {
  getSidebarJobMenuIndex,
  sortSidebarJobsByRecentTime,
  useSidebarJobs,
} from '@/manage/composables/layout/useSidebarJobs'
import { useSidebarPoints } from '@/manage/composables/layout/useSidebarPoints'
import { useSidebarTags } from '@/manage/composables/layout/useSidebarTags'
import type { UserEntitlement, UserJobListPublic, UserSubscription } from '@/manage/types/user'
import { getMembershipVipBadge } from '@/manage/utils/membershipBadgeLabel'
import { getResolvedJobDisplayName } from '@/manage/utils/job'
import { getAgentEntryRoute, getAgentRouteJobId, isAgentRouteName } from '@/manage/utils/agentEntry'
import { notifyError, notifyWarning } from '@/utils/error'
import { LOGTO_SCOPES } from '@/manage/constants/access'
import SidebarAgentIcon from '@/assets/icons/custom/bot.svg'
import SidebarCreditCardIcon from '@/assets/icons/custom/sidebar-credit-card.svg'
import SidebarSettingsIcon from '@/assets/icons/custom/sidebar-settings.svg'
import SidebarUsersIcon from '@/assets/icons/custom/sidebar-users.svg'
import AwaitIcon from '@/assets/icons/custom/await.svg'
import UpgradePlanIcon from '@/assets/icons/custom/upgrade-plan.svg'
import NotificationBellIcon from '@/assets/icons/custom/notification-bell.svg'

const props = defineProps<{
  isCollapse: boolean
}>()

const $route = useRoute()
const router = useRouter()

const userStore = useUserStore()
const { t } = useI18n()
const {
  assetPoints,
  assetAiMatchPoints,
  assetLoading,
  assetIsVip,
  assetVipExpireAt,
  isAuthenticated,
  sessionReady,
  canAdminRead,
  adminMode,
} = storeToRefs(userStore)
const { posthog, hasPostHogKey } = usePostHog()
const enableJobs = featureFlags.jobs
const enableContentManagement = featureFlags.contentManagement
const enableEmailCampaign = featureFlags.emailCampaign
const canEmailInbox = computed(() => userStore.hasScope(LOGTO_SCOPES.emailInbox))
const canEmailOutreach = computed(() => userStore.hasScope(LOGTO_SCOPES.emailOutreach))
// Temporarily hide the lab entry until it is ready to return to the sidebar.
const showAiSourcingLab = false
const AI_SOURCING_MENU_INDEX = 'external:ai-sourcing'
const notificationDrawerVisible = ref(false)
const announcementId = 'double11-2026-11-11'
const announcementRead = ref(false)
const announcementReadHydrated = ref(false)
const isRecentConversationsOpen = ref(true)

const { allCandidatesCount } = useSidebarTags({ t })
const { sidebarJobs, totalJobsCount, fetchSidebarJobs, handleJobMenuSelect } = useSidebarJobs({
  enableJobs,
  t,
})
const { pointsDisplay, isPointsLow } = useSidebarPoints({
  t,
  assetPoints,
  assetLoading,
  assetIsVip,
  assetVipExpireAt,
})
const assetNumberFormatter = new Intl.NumberFormat()

const trackSidebar = (event: string, properties?: Record<string, unknown>) => {
  if (!hasPostHogKey) return
  posthog.capture(event, {
    source: 'global_sidebar',
    is_collapsed: props.isCollapse,
    active_menu: activeMenuIndex.value,
    route_name: String($route.name || ''),
    route_path: $route.path,
    ...properties,
  })
}

const activeMenuIndex = computed(() => {
  // Old and V2 Agent pages alike (step 13).
  if (isAgentRouteName($route.name)) {
    const jobId = getAgentRouteJobId($route)
    return jobId ? getAgentConversationMenuIndex(jobId) : AGENT_NEW_CONVERSATION_MENU_INDEX
  }
  if ($route.name === 'JobCandidates') {
    return getSidebarJobMenuIndex(String($route.params.jobId || ''))
  }
  if ($route.name === 'ResumeParse') {
    const jobId = Array.isArray($route.query.jobId) ? $route.query.jobId[0] : $route.query.jobId
    if (jobId) return getSidebarJobMenuIndex(String(jobId))
    return '/manage/jobs?filter=all'
  }
  if ($route.name === 'JobCreate') {
    const jobId = Array.isArray($route.query.jobId) ? $route.query.jobId[0] : $route.query.jobId
    if (jobId) return getSidebarJobMenuIndex(String(jobId))
    return '/manage/jobs?filter=all'
  }
  if ($route.path === '/manage/content-management') {
    return '/manage/content-management'
  }
  if ($route.path === '/manage/email-config') {
    return '/manage/content-management'
  }
  if ($route.path === '/manage/points-record') {
    return '/manage/content-management'
  }
  if ($route.path === '/manage/cold-email') {
    return '/manage/cold-email'
  }
  if ($route.path.startsWith('/manage/organization')) {
    return '/manage/organization'
  }
  if ($route.path.startsWith('/manage/admin/organizations')) {
    return '/manage/admin/organizations'
  }
  return $route.fullPath
})

const showOrganizationMenu = computed(() =>
  Boolean(userStore.asset?.is_enterprise_mode || userStore.asset?.organization),
)

const isColdEmailActive = computed(() => $route.path === '/manage/cold-email')
const isColdEmailExpanded = ref(isColdEmailActive.value)

watch(
  () => isColdEmailActive.value,
  (active) => {
    // Keep the submenu owned by the current route. Leaving cold email should
    // immediately collapse it so another top-level section does not inherit
    // the previous cold-email state.
    isColdEmailExpanded.value = active
  },
  { immediate: true },
)

const hasSelectedColdEmailCampaign = computed(() => {
  const id = Array.isArray($route.query.id) ? $route.query.id[0] : $route.query.id
  return typeof id === 'string' && id.trim().length > 0
})

const activeColdEmailView = computed(() => {
  if (!isColdEmailActive.value) return null
  const v = $route.query.view
  if (typeof v !== 'string' || !['list', 'edit', 'log', 'set'].includes(v)) return 'list'
  if (v !== 'list' && !hasSelectedColdEmailCampaign.value) return 'list'
  return v
})

const handleColdEmailMenuSelect = () => {
  if ($route.path !== '/manage/cold-email') {
    isColdEmailExpanded.value = true
    trackSidebar('sidebar_menu_clicked', {
      menu: 'cold-email',
      index: '/manage/cold-email',
    })
    void router.push({ path: '/manage/cold-email', query: { view: 'list' } })
  } else {
    isColdEmailExpanded.value = !isColdEmailExpanded.value
    trackSidebar('sidebar_cold_email_toggle', {
      expanded: isColdEmailExpanded.value,
    })
  }
}

const handleColdEmailSubSelect = (view: 'list' | 'edit' | 'log' | 'set') => {
  trackSidebar('sidebar_cold_email_sub_clicked', { view })
  if (view !== 'list' && !hasSelectedColdEmailCampaign.value) {
    notifyWarning(t('coldEmail.messages.selectTaskFirst'))
    void router.replace({ path: '/manage/cold-email', query: { view: 'list' } })
    return
  }
  void router.push({ path: '/manage/cold-email', query: { ...$route.query, view } })
}

const avatarFallbackText = computed(() => {
  const source =
    userStore.user?.name ||
    userStore.user?.username ||
    userStore.user?.email ||
    userStore.displayName
  const firstChar = source?.trim()?.charAt(0)
  return firstChar ? firstChar.toUpperCase() : 'U'
})

const sidebarUserEmail = computed(() => {
  const userInfo = userStore.user as
    | {
        email?: string | null
        primaryEmail?: string | null
        username?: string | null
      }
    | null
    | undefined
  return String(userInfo?.primaryEmail || userInfo?.email || userInfo?.username || '').trim() || '-'
})
const aiMatchPointsDisplay = computed(() =>
  assetNumberFormatter.format(Math.max(0, Math.floor(Number(assetAiMatchPoints.value || 0)))),
)
const notificationUserId = computed(() => {
  const userInfo = userStore.user
  return String(userInfo?.sub || userInfo?.email || userInfo?.username || 'anonymous').trim()
})
const hasNotifications = computed(() => announcementReadHydrated.value && !announcementRead.value)
const recentAgentConversations = computed(() =>
  sortSidebarJobsByRecentTime(sidebarJobs.value).slice(0, 10),
)
const isRecruitingProjectsActive = computed(
  () =>
    activeMenuIndex.value === '/manage/jobs?filter=all' ||
    activeMenuIndex.value.startsWith('/manage/jobs/'),
)
const isTalentPoolActive = computed(() => activeMenuIndex.value.startsWith('/manage/talent-pool'))
const contextJobId = computed(() => {
  if ($route.name === 'JobCandidates') {
    return String($route.params.jobId || '')
  }
  return null
})
// A recent conversation is active on its Agent page (old or V2, `?jobId=`) or its job candidates page.
const isRecentConversationActive = (job: UserJobListPublic) => {
  const jobId = String(job.id)
  return getAgentRouteJobId($route) === jobId || contextJobId.value === jobId
}
type MembershipAccessSource = UserEntitlement | UserSubscription | null | undefined

const normalizeMembershipText = (value: unknown) =>
  String(value || '')
    .trim()
    .toLowerCase()

const isTrialMembershipRecord = (record: MembershipAccessSource) => {
  const status = normalizeMembershipText(record?.status)
  const planName = normalizeMembershipText(record?.plan_name)
  const currentMembership =
    record && 'current_membership' in record
      ? (record.current_membership as UserSubscription['current_membership'])
      : null
  const currentStatus = normalizeMembershipText(currentMembership?.status)
  const currentPlanName = normalizeMembershipText(currentMembership?.plan_name)

  return (
    status === 'trialing' ||
    currentStatus === 'trialing' ||
    planName.includes('trial') ||
    planName.includes('试用') ||
    currentPlanName.includes('trial') ||
    currentPlanName.includes('试用')
  )
}

const isActiveMembershipRecord = (record: MembershipAccessSource) => {
  if (!record || isTrialMembershipRecord(record)) return false
  const status = normalizeMembershipText(record.status)
  const currentMembership =
    'current_membership' in record
      ? (record.current_membership as UserSubscription['current_membership'])
      : null
  const currentStatus = normalizeMembershipText(currentMembership?.status)
  const statuses = [status, currentStatus].filter(Boolean)

  return statuses.length === 0 || statuses.some((item) => !['canceled', 'past_due'].includes(item))
}

const membershipVipBadge = computed(() => getMembershipVipBadge(userStore.asset))

const canOpenAiSourcingLab = computed(() => {
  const currentAsset = userStore.asset
  if (!currentAsset?.is_vip) return false
  if (!currentAsset.entitlement && !currentAsset.subscription) return true
  return (
    isActiveMembershipRecord(currentAsset.entitlement) ||
    isActiveMembershipRecord(currentAsset.subscription)
  )
})

const getJobDisplayName = (job: UserJobListPublic | null | undefined) =>
  getResolvedJobDisplayName(job)

let stopJobListSignalListener: (() => void) | null = null

const syncAnnouncementReadState = () => {
  announcementRead.value = appStorage.isAnnouncementRead(notificationUserId.value, announcementId)
  announcementReadHydrated.value = true
}

const markAnnouncementRead = () => {
  announcementReadHydrated.value = true
  announcementRead.value = true
  appStorage.setAnnouncementRead(notificationUserId.value, announcementId)
}

const openNotificationDrawer = () => {
  notificationDrawerVisible.value = true
  trackSidebar('sidebar_notification_opened', {
    had_unread: hasNotifications.value,
    announcement_id: announcementId,
  })
  markAnnouncementRead()
}

const handleAnnouncementClick = () => {
  trackSidebar('sidebar_notification_announcement_clicked', {
    announcement_id: announcementId,
    target: '/manage/pricing',
  })
  notificationDrawerVisible.value = false
  void router.push('/manage/pricing')
}

onMounted(() => {
  syncAnnouncementReadState()
  stopJobListSignalListener = onJobListSignal(() => {
    void fetchSidebarJobs()
  })
})

watch(notificationUserId, () => {
  syncAnnouncementReadState()
})

watch(
  () => [isAuthenticated.value, sessionReady.value] as const,
  ([authenticated, ready]) => {
    if (!authenticated || !ready) return
    void userStore.fetchAsset()
  },
  { immediate: true },
)

onUnmounted(() => {
  stopJobListSignalListener?.()
  stopJobListSignalListener = null
})

const handleMenuSelect = async (index: string) => {
  if (index === AI_SOURCING_MENU_INDEX) {
    await userStore.fetchAsset(true)
    trackSidebar('sidebar_ai_sourcing_lab_clicked', {
      allowed: canOpenAiSourcingLab.value,
      is_vip: assetIsVip.value,
      entitlement_provider: userStore.asset?.entitlement?.provider || undefined,
      subscription_provider: userStore.asset?.subscription?.provider || undefined,
      subscription_channel: userStore.asset?.subscription?.channel || undefined,
      subscription_status: userStore.asset?.subscription?.status || undefined,
    })
    if (!canOpenAiSourcingLab.value) {
      notifyWarning(t('recruitingAi.messages.aiSourcingMembershipRequired'))
      await router.push({ name: 'ManagePricing', query: { tab: 'pro' } })
      return
    }
    window.open(websiteLinks.externalTools.aiSourcing, '_blank', 'noopener,noreferrer')
    return
  }

  if (index === $route.fullPath) {
    trackSidebar('sidebar_menu_reclicked', {
      index,
    })
    return
  }
  if (await handleJobMenuSelect(index)) {
    trackSidebar('sidebar_menu_clicked', {
      menu: 'job',
      index,
    })
    return
  }
  trackSidebar('sidebar_menu_clicked', {
    menu: 'route',
    index,
  })
  router.push(index)
}

// Menu identities for the active-menu tracking; navigation goes through the step 13 entry rule.
const AGENT_NEW_CONVERSATION_MENU_INDEX = 'agent:new-conversation'
const getAgentConversationMenuIndex = (jobId: string) => `agent:conversation:${jobId}`

const handleNewConversation = async () => {
  trackSidebar('sidebar_menu_clicked', {
    menu: 'new_recruiting_conversation',
    index: '/manage/recruiting-ai-v2',
  })
  await router.push(getAgentEntryRoute(null))
}

const handleRecentConversationSelect = async (job: UserJobListPublic) => {
  trackSidebar('sidebar_recent_conversation_clicked', {
    job_id: String(job.id),
    job_name: getJobDisplayName(job) || undefined,
  })
  await router.push(getAgentEntryRoute(job, { profile: getJobDisplayName(job) || undefined }))
}

const refreshPage = () => {
  trackSidebar('sidebar_logo_refresh_clicked')
  location.reload()
}

const handleToggleAdminMode = async () => {
  if (adminMode.value) {
    trackSidebar('sidebar_admin_mode_toggled', {
      next_enabled: false,
    })
    userStore.setAdminMode(false)
    return
  }

  try {
    // Refresh permission claims before enabling admin mode.
    await userStore.forceRefreshDefaultToken()
  } catch (error) {
    trackSidebar('sidebar_admin_mode_toggle_failed', {
      reason: 'token_refresh_failed',
      error_message: error instanceof Error ? error.message : String(error || ''),
    })
    notifyError({
      scope: 'sidebar enable admin mode failed',
      error,
      message: t('error.requestFailed'),
    })
    return
  }

  if (!userStore.canAdminRead) {
    trackSidebar('sidebar_admin_mode_toggle_blocked', {
      reason: 'no_permission',
    })
    notifyWarning(t('error.noPermission'))
    userStore.setAdminMode(false)
    return
  }

  trackSidebar('sidebar_admin_mode_toggled', {
    next_enabled: true,
  })
  userStore.setAdminMode(true)
}

const handleUserCommand = async (command: string) => {
  if (command === 'upgrade') {
    if (hasPostHogKey) {
      posthog.capture('upgrade_cta_clicked', {
        source: 'sidebar_dropdown',
      })
    }
    router.push({ name: 'ManagePricing' })
  } else if (command === 'profile') {
    if (hasPostHogKey) {
      posthog.capture('profile_opened', {
        source: 'sidebar_dropdown',
      })
    }
    router.push({ name: 'SettingsProfile' })
  } else if (command === 'referral') {
    trackSidebar('referral_opened', {
      source: 'sidebar_dropdown',
    })
    void router.push({ name: 'ManageReferral' })
  } else if (command === 'points-record') {
    trackSidebar('points_record_opened', {
      source: 'sidebar_dropdown',
    })
    if (userStore.asset?.is_enterprise_mode) {
      void router.push('/manage/organization')
    } else {
      void router.push({ name: 'ContentManagement', query: { module: 'points' } })
    }
  }
}

// 监听路由变化，滚动到当前激活的菜单项
watch(
  () => $route.fullPath,
  () => {
    if (enableJobs && $route.name === 'Jobs' && sidebarJobs.value.length === 0) {
      void fetchSidebarJobs()
    }
  },
)
</script>

<style scoped>
.sidebar-wrapper {
  --sidebar-scrollbar-size: 7px;
  --sidebar-scrollbar-radius: 999px;
  --sidebar-scrollbar-track: rgba(226, 232, 240, 0.72);
  --sidebar-scrollbar-thumb: rgba(148, 163, 184, 0.52);
  --sidebar-scrollbar-thumb-hover: rgba(100, 116, 139, 0.72);
  --sidebar-scrollbar-thumb-active: rgba(71, 85, 105, 0.82);
  height: 100%;
  display: flex;
  flex-direction: column;
  border-right: 1px solid #e5e7eb;
  background: #f5f5f5;
}

.sidebar-header,
.sidebar-content,
.sidebar-footer {
  padding: 0;
  gap: 0;
}

.sidebar-nav {
  flex: 1;
  min-height: 0;
  overflow: hidden;
}

.sidebar-nav :deep([data-slot='scroll-area-viewport']) {
  overflow-x: hidden !important;
}

.sidebar-nav :deep([data-slot='scroll-area-viewport'] > div) {
  width: 100% !important;
  min-width: 0 !important;
  display: block !important;
}

.sidebar-nav__view {
  width: 100%;
  min-width: 0;
  box-sizing: border-box;
  padding: 8px 10px 10px;
}

.sidebar-nav--collapsed .sidebar-nav__view {
  padding: 6px 5px 10px;
}

.sidebar-create-button,
.sidebar-nav-item,
.sidebar-sub-item,
.sidebar-recent-item {
  width: 100%;
  border: 0;
  background: transparent;
  font-family: inherit;
  text-align: left;
  cursor: pointer;
}

.sidebar-top-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.sidebar-create-button {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  min-height: 38px;
  padding: 0 12px;
  border-radius: 9px;
  background: #1677ff;
  box-shadow: 0 7px 16px rgba(22, 119, 255, 0.22);
  color: #fff;
  font-size: 14px;
  font-weight: 700;
  flex: 1;
  min-width: 0;
  transition:
    background-color 0.18s ease,
    box-shadow 0.18s ease,
    transform 0.18s ease;
}

.sidebar-notification-button {
  position: relative;
  flex-shrink: 0;
  width: 38px;
  height: 38px;
  padding: 0;
  border: 0;
  border-radius: 999px;
  background: #eef0f3;
  color: #111827;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition:
    background-color 0.18s ease,
    color 0.18s ease;
}

.sidebar-notification-button:hover {
  background: #fff;
  color: #0052d9;
}

.sidebar-notification-badge {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  line-height: 1;
}

.sidebar-notification-badge__content {
  position: absolute;
  top: -3px;
  right: -4px;
  width: 8px;
  height: 8px;
  border: 2px solid #eef0f3;
  border-radius: 999px;
  background: #ef4444;
  box-sizing: content-box;
}

.sidebar-create-button:hover {
  background: #0052d9;
  box-shadow: 0 9px 19px rgba(0, 82, 217, 0.26);
}

.sidebar-create-button:active {
  transform: translateY(1px);
}

.sidebar-nav--collapsed .sidebar-create-button {
  width: 38px;
  min-height: 38px;
  margin: 0 auto;
  padding: 0;
}

.sidebar-nav--collapsed .sidebar-top-actions {
  flex-direction: column;
  gap: 6px;
}

.sidebar-section {
  margin-top: 6px;
}

.sidebar-section--recent {
  margin-top: 10px;
}

.sidebar-section__title {
  margin: 0 0 0 4px;
  padding-right: 0;
  padding-left: 0;
  color: #8b8f9a;
  font-size: 12px;
  font-weight: 700;
  line-height: 1.2;
}

.sidebar-section__title--toggle {
  display: flex;
  align-items: center;
  gap: 4px;
  height: 18px;
  max-width: calc(100% - 4px);
  padding: 0;
  border: 0;
  background: transparent;
  text-align: left;
  cursor: pointer;
  transition: color 0.18s ease;
  margin-left: 4px;
}

.sidebar-section__title--toggle > span {
  display: inline-flex;
  align-items: center;
  height: 18px;
  line-height: 18px;
}

.sidebar-section__title--toggle:hover {
  color: #5f6673;
}

.sidebar-section__title-chevron {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 12px;
  height: 12px;
  color: currentcolor;
  font-size: 12px;
  line-height: 1;
  transform: rotate(-90deg);
  transform-origin: center;
  transition: transform 0.18s ease;
}

.sidebar-section__title-chevron.is-open {
  transform: rotate(0deg);
}

.sidebar-nav-item {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  min-height: 36px;
  padding: 0 9px;
  border-radius: 9px;
  color: #4b5563;
  font-size: 13px;
  line-height: 1.2;
  transition:
    background-color 0.18s ease,
    box-shadow 0.18s ease,
    color 0.18s ease;
}

.sidebar-nav-item + .sidebar-nav-item {
  margin-top: 3px;
}

.sidebar-nav-item:hover,
.sidebar-nav-item.is-active {
  background: #fff;
  box-shadow: 0 5px 14px rgba(15, 23, 42, 0.08);
  color: #111827;
}

.sidebar-nav-item.is-active {
  font-weight: 700;
}

.sidebar-sub-menu {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 2px;
  margin: 3px 0 5px 22px;
  padding: 2px 0 2px 10px;
  border-left: 1.5px solid #e2e8f0;
}

.sidebar-sub-item {
  position: relative;
  width: 100%;
  display: flex;
  align-items: center;
  gap: 7px;
  padding: 6px 9px;
  border-radius: 6px;
  border: 0;
  background: transparent;
  color: #64748b;
  font-size: 12.5px;
  font-weight: 500;
  line-height: 1.3;
  text-align: left;
  cursor: pointer;
  transition:
    background-color 0.15s ease,
    color 0.15s ease;
}

.sidebar-sub-item:hover {
  background: #f8fafc;
  color: #0f172a;
}

.sidebar-sub-item.is-active {
  background: #eff6ff;
  color: #2563eb;
  font-weight: 600;
}

.sidebar-sub-item.is-active::before {
  content: '';
  position: absolute;
  left: -11.5px;
  top: 50%;
  transform: translateY(-50%);
  width: 2px;
  height: 16px;
  background: #2563eb;
  border-radius: 99px;
}

.sidebar-sub-item__icon {
  width: 13.5px;
  height: 13.5px;
  flex-shrink: 0;
  opacity: 0.75;
  transition: opacity 0.15s ease;
}

.sidebar-sub-item:hover .sidebar-sub-item__icon,
.sidebar-sub-item.is-active .sidebar-sub-item__icon {
  opacity: 1;
}

.sidebar-nav-item__chevron {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 14px;
  height: 14px;
  color: #9ca3af;
  transform: rotate(-90deg);
  transition:
    transform 0.2s ease,
    color 0.18s ease;
}

.sidebar-nav-item__chevron.is-open {
  transform: rotate(0deg);
}

.sidebar-nav-item:hover .sidebar-nav-item__chevron {
  color: #111827;
}

.sidebar-sub-fade-enter-active,
.sidebar-sub-fade-leave-active {
  transition:
    opacity 0.18s ease,
    transform 0.18s ease;
}

.sidebar-sub-fade-enter-from,
.sidebar-sub-fade-leave-to {
  opacity: 0;
  transform: translateY(-4px);
}

.sidebar-nav-item.is-restricted {
  color: #6b7280;
}

.sidebar-nav-item.is-restricted .sidebar-nav-item__beta {
  background: #fff7ed;
  color: #c2410c;
}

.sidebar-button-icon,
.sidebar-nav-item__icon {
  flex-shrink: 0;
  width: 16px;
  height: 16px;
  color: currentcolor;
}

.sidebar-nav-item__icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.sidebar-nav-item__label {
  min-width: 0;
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.sidebar-nav-item__badge,
.sidebar-nav-item__beta {
  display: inline-flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  min-width: 24px;
  height: 24px;
  padding: 0 7px;
  border-radius: 999px;
  background: #eef0f3;
  color: #8b8f9a;
  font-size: 11px;
  font-weight: 700;
  line-height: 1;
  font-variant-numeric: tabular-nums;
}

.sidebar-nav-item__beta {
  min-width: 40px;
  height: 24px;
  padding: 0;
  background: #e6f0ff;
  color: #0052d9;
}

.sidebar-recent-item {
  display: block;
  min-width: 0;
  padding: 5px 7px 5px 8px;
  overflow: hidden;
  border-radius: 8px;
  color: #4b5563;
  font-size: 12px;
  line-height: 1.35;
  text-overflow: ellipsis;
  white-space: nowrap;
  transition:
    background-color 0.18s ease,
    color 0.18s ease;
}

.sidebar-recent-list {
  max-height: 268px;
  overflow-y: auto;
  overscroll-behavior: contain;
  padding-right: 2px;
  scrollbar-width: thin;
  scrollbar-color: rgba(148, 163, 184, 0.28) transparent;
}

.sidebar-recent-list::-webkit-scrollbar {
  width: 4px;
}

.sidebar-recent-list::-webkit-scrollbar-track {
  background: transparent;
}

.sidebar-recent-list::-webkit-scrollbar-thumb {
  border-radius: 999px;
  background: rgba(148, 163, 184, 0.28);
}

.sidebar-recent-list:hover::-webkit-scrollbar-thumb {
  background: rgba(100, 116, 139, 0.46);
}

.sidebar-recent-item:hover {
  background: #fff;
  color: #111827;
}

.sidebar-recent-item.is-active {
  background: var(--brand-light-blue, #e6f0ff);
  color: var(--brand-blue, #0052d9);
  font-weight: 700;
}

.sidebar-nav--collapsed .sidebar-section {
  margin-top: 14px;
}

.sidebar-nav--collapsed .sidebar-nav-item {
  justify-content: center;
  width: 38px;
  min-height: 38px;
  margin-right: auto;
  margin-left: auto;
  padding: 0;
}

.sidebar-admin-mode {
  flex-shrink: 0;
  padding: 6px 10px 4px;
  background: #f5f5f5;
}

.sidebar-admin-mode__button {
  display: flex;
  align-items: center;
  width: calc(100% - 8px);
  min-height: 34px;
  margin: 0 auto;
  gap: 8px;
  padding: 0 9px;
  border: 1px solid #e5e7eb;
  border-radius: 9px;
  background: #fff;
  color: #64748b;
  font-family: inherit;
  font-size: 12px;
  line-height: 1;
  cursor: pointer;
  transition:
    background-color 0.18s ease,
    border-color 0.18s ease,
    color 0.18s ease;
}

.sidebar-admin-mode__button:hover {
  border-color: #d1d5db;
  color: #111827;
}

.sidebar-admin-mode__button.is-active {
  border-color: #fed7aa;
  background: #fff7ed;
  color: #c2410c;
}

.sidebar-admin-mode__label {
  min-width: 0;
  flex: 1;
  overflow: hidden;
  text-align: left;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.sidebar-admin-mode__state {
  flex-shrink: 0;
  font-weight: 700;
}

.sidebar-admin-mode--collapsed {
  display: flex;
  justify-content: center;
  padding: 6px 0 4px;
}

.sidebar-admin-mode--collapsed .sidebar-admin-mode__button {
  justify-content: center;
  width: 38px;
  min-height: 34px;
  padding: 0;
}

.sidebar-user {
  flex-shrink: 0;
  padding: 0 10px 12px;
  background: #f5f5f5;
}

.sidebar-user__trigger {
  display: flex;
  align-items: center;
  width: 100%;
  min-width: 0;
  min-height: 46px;
  padding: 7px;
  border: 0;
  border-radius: 9px;
  background: transparent;
  color: #111827;
  font: inherit;
  text-align: left;
  cursor: pointer;
  transition:
    background-color 0.18s ease,
    box-shadow 0.18s ease;
}

.sidebar-user__trigger:hover {
  background: #f9fafb;
}

.sidebar-user__trigger:focus-visible {
  outline: 2px solid rgba(0, 82, 217, 0.24);
  outline-offset: 2px;
}

.sidebar-user--collapsed {
  display: flex;
  justify-content: center;
  padding: 0 0 14px;
}

.sidebar-user--collapsed .sidebar-user__trigger {
  justify-content: center;
  width: 46px;
  min-height: 46px;
  padding: 7px;
}

.sidebar-user__avatar-badge-wrapper {
  position: relative;
  display: inline-flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
}

.sidebar-user__avatar {
  background: #2563eb;
  color: #fff;
  font-weight: 700;
}

.sidebar-user__avatar-fallback {
  color: #fff;
  font-size: 13px;
  line-height: 1;
}

.sidebar-user__membership-badge {
  position: absolute;
  z-index: 1;
  bottom: -10px;
  left: 50%;
  max-width: 96px;
  overflow: hidden;
  padding: 1px 6px;
  transform: translateX(-50%);
  border: 1px solid #fff;
  border-radius: 999px;
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.2);
  font-size: 8px;
  font-weight: 700;
  line-height: 12px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* 试用会员: VIP 淡蓝色 */
.sidebar-user__membership-badge--trial {
  border-color: #bae6fd;
  background: linear-gradient(135deg, #e0f2fe 0%, #bae6fd 100%);
  color: #0369a1;
  box-shadow: 0 1px 3px rgba(14, 165, 233, 0.25);
}

/* 新手会员: VIP 深蓝色 */
.sidebar-user__membership-badge--starter,
.sidebar-user__membership-badge--basic {
  border-color: #93c5fd;
  background: linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%);
  color: #ffffff;
  box-shadow: 0 1px 4px rgba(29, 78, 216, 0.35);
}

/* 专业版会员月付: PRO 淡蓝色 */
.sidebar-user__membership-badge--pro-monthly,
.sidebar-user__membership-badge--professional {
  border-color: #bae6fd;
  background: linear-gradient(135deg, #e0f2fe 0%, #93c5fd 100%);
  color: #1d4ed8;
  box-shadow: 0 1px 3px rgba(37, 99, 235, 0.25);
}

/* 专业版会员年费: PRO 深蓝色 */
.sidebar-user__membership-badge--pro-annual,
.sidebar-user__membership-badge--pro {
  border-color: #bfdbfe;
  background: linear-gradient(135deg, #1e40af 0%, #172554 100%);
  color: #ffffff;
  box-shadow: 0 1px 4px rgba(30, 64, 175, 0.45);
}

/* 企业版会员: Enterprise 金黄色 */
.sidebar-user__membership-badge--enterprise,
.sidebar-user__membership-badge--business {
  border-color: #fde047;
  background: linear-gradient(135deg, #fef08a 0%, #f59e0b 100%);
  color: #78350f;
  box-shadow: 0 1px 5px rgba(245, 158, 11, 0.4);
  letter-spacing: 0.01em;
}

.sidebar-user__body {
  display: flex;
  flex: 1;
  min-width: 0;
  flex-direction: column;
  gap: 2px;
  margin-left: 8px;
}

.sidebar-user__name,
.sidebar-user__email {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.sidebar-user__name {
  color: #111827;
  font-size: 13px;
  font-weight: 700;
  line-height: 1.35;
}

.sidebar-user__email {
  color: #64748b;
  font-size: 11px;
  line-height: 1.35;
}

.sidebar-user__menu {
  width: 220px;
  min-width: 220px;
}

.sidebar-user__menu-summary {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 14px 10px;
}

.sidebar-user__menu-copy {
  min-width: 0;
  flex: 1;
}

.sidebar-user__menu-name {
  max-width: 160px;
  overflow: hidden;
  color: #0f172a;
  font-size: 14px;
  font-weight: 700;
  line-height: 1.4;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.sidebar-user__menu-email {
  max-width: 160px;
  overflow: hidden;
  margin-top: 2px;
  color: #64748b;
  font-size: 12px;
  line-height: 1.45;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.sidebar-user__menu-value {
  margin-left: auto;
  flex-shrink: 0;
  max-width: 72px;
  overflow: hidden;
  color: #0052d9;
  font-size: 13px;
  font-weight: 700;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
}

.sidebar-user__menu-item {
  min-width: 0;
  white-space: nowrap;
}

.sidebar-user__menu-item > span:not(.sidebar-user__menu-value) {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.sidebar-user__menu-value.is-low {
  color: #b45309;
}

.sidebar-user__menu-icon {
  margin-right: 8px;
  color: #64748b;
  font-size: 15px;
}

.sidebar-user__upgrade-icon {
  color: #0052d9;
}

.sidebar-user__points-icon {
  color: #64748b;
}

.sidebar-user__menu-item:hover .sidebar-user__upgrade-icon {
  color: #0040b0;
}

.notification-drawer {
  display: flex;
  min-height: 100%;
  flex-direction: column;
  background: #fff;
  color: #111827;
}

.notification-drawer__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 22px 22px 18px;
  border-bottom: 1px solid #eef2f7;
}

.notification-drawer__header h2 {
  margin: 0;
  color: #111827;
  font-size: 16px;
  font-weight: 700;
  line-height: 1.25;
}

.notification-drawer__close {
  width: 28px;
  height: 28px;
  border: 0;
  border-radius: 8px;
  background: transparent;
  color: #111827;
  font-family: inherit;
  font-size: 16px;
  font-weight: 800;
  line-height: 1;
  cursor: pointer;
}

.notification-drawer__close:hover {
  background: #f5f5f5;
}

.notification-item {
  border: 0;
  background: transparent;
  font-family: inherit;
  cursor: pointer;
}

.notification-item {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  column-gap: 14px;
  width: 100%;
  height: auto;
  padding: 16px 22px 18px;
  border-radius: 0;
  border-bottom: 1px solid #eef2f7;
  color: inherit;
  text-align: left;
  white-space: normal;
  transition: background-color 0.18s ease;
}

.notification-item:hover {
  background: #f9fafb;
}

.notification-item__content {
  min-width: 0;
}

.notification-item__title {
  overflow: hidden;
  color: #1f2937;
  font-size: 13px;
  font-weight: 600;
  line-height: 1.45;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.notification-item__desc {
  display: -webkit-box;
  overflow: hidden;
  margin-top: 4px;
  color: #6b7280;
  font-size: 12px;
  line-height: 1.5;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  line-clamp: 2;
}

.notification-item__time {
  flex-shrink: 0;
  color: #7c7c7c;
  font-size: 12px;
  line-height: 1.45;
  white-space: nowrap;
}

@media (max-width: 720px) {
  .sidebar-notification-drawer {
    width: calc(100vw - 16px);
  }
}

/* ScrollArea keeps the original content layout inside its viewport. */
.sidebar-recent-list {
  display: flex;
  flex-direction: column;
  min-height: 0;
  padding: 0;
  overflow: hidden !important;
  min-width: 0;
}
.sidebar-recent-list > :deep([data-slot='scroll-area-viewport'] > div) {
  min-height: 100%;
}
.sidebar-recent-list > :deep([data-slot='scroll-area-viewport'] > div) {
  padding-right: 2px !important;
}
</style>
